import $ from 'jquery'

import './styles/contact-form.scss'

const config = window.mint_contact_forms || {}

const TURNSTILE_CALLBACK = 'mintContactFormsTurnstileReady'

let turnstilePromise = null

/**
 * The Cloudflare script is only injected once the visitor touches a form, so
 * pages carrying a form still load without a third party request.
 */
function loadTurnstile() {
    if (turnstilePromise) {
        return turnstilePromise
    }

    turnstilePromise = new Promise(function (resolve, reject) {
        if (window.turnstile) {
            resolve(window.turnstile)
            return
        }

        window[TURNSTILE_CALLBACK] = function () {
            resolve(window.turnstile)
        }

        const script = document.createElement('script')

        script.src = config.script_url + '?render=explicit&onload=' + TURNSTILE_CALLBACK
        script.async = true
        script.defer = true
        script.onerror = function () {
            reject(new Error('turnstile-load-failed'))
        }

        document.head.appendChild(script)
    })

    return turnstilePromise
}

function MintForm(element) {
    this.$form = $(element)
    this.slug = this.$form.data('mint-slug')
    this.redirect = this.$form.data('mint-redirect') || ''
    this.$status = this.$form.find('[data-mint-status]')
    this.$turnstile = this.$form.find('[data-mint-turnstile]')
    this.$submit = this.$form.find('[type="submit"]')

    this.credentials = null
    this.widgetId = null
    this.token = ''
    this.armed = false
    this.submitting = false
    this.pendingSubmit = false
    this.pendingTimer = null

    this.bind()
}

MintForm.prototype.bind = function () {
    const self = this

    this.$form.on('focusin.mintform pointerdown.mintform keydown.mintform', function () {
        self.arm()
    })

    this.$form.on('submit.mintform', function (event) {
        event.preventDefault()
        self.handleSubmit()
    })

    this.$form.on('input change', '[name]', function () {
        self.clearFieldError($(this).attr('name'))
    })
}

/**
 * Fetches the nonce, site key and dwell token, then renders the widget. The
 * nonce never sits in cached HTML, so full page caching cannot stale it.
 */
MintForm.prototype.arm = function () {
    const self = this

    if (this.armed) {
        return this.armed
    }

    this.armed = this.prime()
        .then(function () {
            return loadTurnstile()
        })
        .then(function (turnstile) {
            self.renderWidget(turnstile)
        })
        .catch(function () {
            self.armed = null
        })

    return this.armed
}

MintForm.prototype.prime = function () {
    const self = this

    return $.post(config.ajaxurl, {
        action: config.prime_action,
        slug: this.slug,
    }).then(function (response) {
        if (!response || !response.success) {
            return $.Deferred().reject().promise()
        }

        self.credentials = response.data

        return response.data
    })
}

MintForm.prototype.renderWidget = function (turnstile) {
    const self = this

    if (!turnstile || !this.$turnstile.length || this.widgetId !== null || !this.credentials) {
        return
    }

    const options = this.credentials.turnstile || {}

    this.widgetId = turnstile.render(this.$turnstile.get(0), {
        sitekey: this.credentials.sitekey,
        action: this.slug,
        theme: options.theme,
        size: options.size,
        appearance: options.appearance,
        language: options.language,
        callback: function (token) {
            self.token = token

            if (self.pendingSubmit) {
                self.pendingSubmit = false
                self.send()
            }
        },
        'expired-callback': function () {
            self.token = ''
        },
        'error-callback': function () {
            self.token = ''
            self.pendingSubmit = false
            self.setBusy(false)
            self.setStatus(config.i18n.generic_error, 'error')
        },
    })
}

MintForm.prototype.handleSubmit = function () {
    const self = this

    if (this.submitting) {
        return
    }

    this.clearErrors()
    this.setBusy(true)

    $.when(this.arm()).always(function () {
        if (self.token) {
            self.send()
            return
        }

        // The widget has not solved yet; the token callback will finish the job.
        self.pendingSubmit = true
        self.setStatus(config.i18n.verifying, 'pending')
        self.watchPendingSubmit()
    })
}

/**
 * Stops the form locking up if the challenge never resolves.
 */
MintForm.prototype.watchPendingSubmit = function () {
    const self = this

    window.clearTimeout(this.pendingTimer)

    this.pendingTimer = window.setTimeout(function () {
        if (!self.pendingSubmit) {
            return
        }

        self.pendingSubmit = false
        self.setBusy(false)
        self.setStatus(config.i18n.generic_error, 'error')
    }, 20000)
}

MintForm.prototype.send = function () {
    const self = this

    if (!this.credentials) {
        this.setBusy(false)
        this.setStatus(config.i18n.generic_error, 'error')
        return
    }

    this.submitting = true
    this.setStatus(config.i18n.sending, 'pending')

    const data = new FormData(this.$form.get(0))

    data.set('action', config.submit_action)
    data.set('slug', this.slug)
    data.set('nonce', this.credentials.nonce)
    data.set('mint_ts', this.credentials.ts)
    data.set('cf-turnstile-response', this.token)
    data.set('page_url', window.location.href)
    data.set('page_title', document.title)

    $.ajax({
        url: config.ajaxurl,
        method: 'POST',
        data: data,
        processData: false,
        contentType: false,
    }).done(function (response) {
        self.handleSuccess(response)
    }).fail(function (xhr) {
        self.handleFailure(xhr)
    }).always(function () {
        self.submitting = false
        self.resetToken()
    })
}

MintForm.prototype.handleSuccess = function (response) {
    const data = (response && response.data) || {}

    this.setBusy(false)
    this.$form.trigger('mint:success', [data])

    if (data.redirect) {
        window.location.assign(data.redirect)
        return
    }

    this.$form.get(0).reset()
    this.setStatus(data.message || '', 'success')
    this.prime()
}

MintForm.prototype.handleFailure = function (xhr) {
    const data = (xhr && xhr.responseJSON && xhr.responseJSON.data) || {}

    this.setBusy(false)
    this.setStatus(data.message || config.i18n.generic_error, 'error')
    this.$form.trigger('mint:error', [data])

    if (!data.errors) {
        return
    }

    let first = null

    for (const key in data.errors) {
        if (!Object.prototype.hasOwnProperty.call(data.errors, key)) {
            continue
        }

        const $field = this.showFieldError(key, data.errors[key])

        if (!first && $field && $field.length) {
            first = $field
        }
    }

    if (first) {
        first.trigger('focus')
    }
}

MintForm.prototype.showFieldError = function (key, message) {
    const $field = this.findField(key)
    let $error = this.$form.find('[data-mint-error-for="' + key + '"]')

    if (!$error.length) {
        $error = $('<span class="mint-form__error"></span>').attr('data-mint-error-for', key)

        const $holder = $field.closest('.mint-form__field')

        if ($holder.length) {
            $holder.append($error)
        } else {
            $field.after($error)
        }
    }

    $error.text(message)
    $field.attr('aria-invalid', 'true')

    return $field
}

MintForm.prototype.clearFieldError = function (name) {
    if (!name) {
        return
    }

    const key = name.replace(/\[\]$/, '')

    this.$form.find('[data-mint-error-for="' + key + '"]').remove()
    this.findField(key).removeAttr('aria-invalid')
}

MintForm.prototype.clearErrors = function () {
    this.$form.find('[data-mint-error-for]').remove()
    this.$form.find('[aria-invalid]').removeAttr('aria-invalid')
    this.setStatus('', '')
}

MintForm.prototype.findField = function (key) {
    return this.$form.find('[name="' + key + '"], [name="' + key + '[]"]').first()
}

MintForm.prototype.resetToken = function () {
    this.token = ''

    if (window.turnstile && this.widgetId !== null) {
        window.turnstile.reset(this.widgetId)
    }
}

MintForm.prototype.setBusy = function (busy) {
    this.$form.toggleClass('is-busy', !!busy)
    this.$submit.prop('disabled', !!busy)
}

MintForm.prototype.setStatus = function (message, state) {
    if (!this.$status.length) {
        return
    }

    this.$status
        .removeClass('is-error is-success is-pending')
        .text(message || '')

    if (state) {
        this.$status.addClass('is-' + state)
    }
}

$(function () {
    $('[data-mint-form]').each(function () {
        if (!$(this).data('mintFormInstance')) {
            $(this).data('mintFormInstance', new MintForm(this))
        }
    })
})
